const padTwoDigits = (value: number): string => String(value).padStart(2, "0");

const formatDate = (date: Date): string =>
  `${date.getFullYear()}-${padTwoDigits(date.getMonth() + 1)}-${padTwoDigits(date.getDate())}`;

const getMonthBounds = (date: Date): { startDate: Date; endDate: Date } => {
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return { startDate, endDate };
};

export const getCurrentMonthDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const { startDate, endDate } = getMonthBounds(today);
  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
};

export const getMonthDateRange = (date: Date): { startDate: string; endDate: string } => {
  const { startDate, endDate } = getMonthBounds(date);
  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
};

const MONTH_NAMES_PT = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
const MONTH_NAMES_EN = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const MONTH_NAMES_ES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const getMonthNames = (locale: string): string[] => {
  if (locale.startsWith("en")) return MONTH_NAMES_EN;
  if (locale.startsWith("es")) return MONTH_NAMES_ES;
  return MONTH_NAMES_PT;
};

export const getLast12MonthsLabels = (locale = "pt-BR"): string[] => {
  const monthNames = getMonthNames(locale);
  const months: string[] = [];
  const now = new Date();

  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(monthNames[date.getMonth()]);
  }

  return months;
};

export const normalizeLast12MonthsData = <T extends { month: string }>(
  data: T[],
  defaultValues: Omit<T, "month">,
  locale = "pt-BR"
): T[] => {
  const now = new Date();
  const last12Months: T[] = [];
  const displayNames = getMonthNames(locale);

  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthIndex = date.getMonth();
    const ptKey = MONTH_NAMES_PT[monthIndex];
    const displayKey = displayNames[monthIndex];

    const existingData = data.find((item) => {
      const itemMonth = item.month.toUpperCase().substring(0, 3);
      return itemMonth === ptKey;
    });

    if (existingData) {
      last12Months.push({ ...existingData, month: displayKey } as T);
    } else {
      last12Months.push({ month: displayKey, ...defaultValues } as T);
    }
  }

  return last12Months;
};

export function formatDateRange(startDate: Date, endDate: Date, locale = "pt-BR", periodLabel?: string): string {
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const startMonth = startDate.getMonth();
  const endMonth = endDate.getMonth();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  const daysInMonth = new Date(endYear, endMonth + 1, 0).getDate();
  const sameMonth = startMonth === endMonth && startYear === endYear;
  const isStartOfMonth = startDay === 1;
  const isEndOfMonth = endDay === daysInMonth;

  if (sameMonth && isStartOfMonth && isEndOfMonth) {
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
    });

    const formatted = formatter.format(startDate);
    const monthName = formatted.split(" ")[0];
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

    return `${capitalizedMonth.toUpperCase()} ${startYear}`;
  }

  const formatDay = (d: Date) => d.toLocaleDateString(locale, { timeZone: "UTC" });

  if (periodLabel) return periodLabel.replace("{start}", formatDay(startDate)).replace("{end}", formatDay(endDate));

  return `${formatDay(startDate)} - ${formatDay(endDate)}`;
}
